import { useMemo, useRef } from 'react';
import { useFragment, graphql } from 'react-relay';

import { useShowVimeoVideo } from './useShowVimeoVideo.ts';

import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { type ArrayElement } from 'dibs-ts-utils/exports/ArrayElement.ts';
import { type useItemMediaItems_viewer$key } from './__generated__/useItemMediaItems_viewer.graphql.ts';
import { type useItemMediaItems_item$key } from './__generated__/useItemMediaItems_item.graphql.ts';
import {
    type useItemMediaItems_sortedPhotos$key,
    type useItemMediaItems_sortedPhotos$data,
} from './__generated__/useItemMediaItems_sortedPhotos.graphql.ts';
import { type useItemMediaItems_videoVimeo$key } from './__generated__/useItemMediaItems_videoVimeo.graphql.ts';
import { type useItemMediaItems_viewInRoomPhotos$key } from './__generated__/useItemMediaItems_viewInRoomPhotos.graphql.ts';
import { type useItemMediaItems_muxVideo$key } from './__generated__/useItemMediaItems_muxVideo.graphql.ts';
import { encodeImageUrl } from './helpers.ts';

const viewerFragment = graphql`
    fragment useItemMediaItems_viewer on Viewer {
        ...useShowVimeoVideo_viewer
    }
`;

const itemFragment = graphql`
    fragment useItemMediaItems_item on Item {
        ...useShowVimeoVideo_item
        isMultiSku
    }
`;

const sortedPhotosFragment = graphql`
    fragment useItemMediaItems_sortedPhotos on ItemPhoto @relay(plural: true) {
        __typename
        largePath
        masterOrZoomPath
        imageSkuXrefs {
            skuId
        }
    }
`;

const vimeoVideFragment = graphql`
    fragment useItemMediaItems_videoVimeo on VideoVimeo {
        __typename
    }
`;

const muxVideoFragment = graphql`
    fragment useItemMediaItems_muxVideo on VideoV2 {
        __typename
    }
`;

const viewInRoomPhotosFragment = graphql`
    fragment useItemMediaItems_viewInRoomPhotos on viewInRoomPhotos @relay(plural: true) {
        __typename
    }
`;

type ItemMediaVideoKey = useItemMediaItems_videoVimeo$key | useItemMediaItems_muxVideo$key;

//Photos generic type
export type MediaItemPhoto<
    Photos extends useItemMediaItems_sortedPhotos$key = useItemMediaItems_sortedPhotos$key,
> = ArrayElement<Photos> & {
    __type: 'photo';
};
type MediaItemPhotos<
    Photos extends useItemMediaItems_sortedPhotos$key = useItemMediaItems_sortedPhotos$key,
> = MediaItemPhoto<Photos>[];

//Video generic type
export type MediaItemVideo<Video extends ItemMediaVideoKey = ItemMediaVideoKey> = Video & {
    __type: 'video';
};

//ViewInRoom generic type
export type MediaItemViewInRoom<
    ViewInRoom extends useItemMediaItems_viewInRoomPhotos$key =
        useItemMediaItems_viewInRoomPhotos$key,
> = ArrayElement<ViewInRoom> & {
    __type: 'viewInRoom';
};

//MediaItem generic type
type MediaItem<
    Photos extends useItemMediaItems_sortedPhotos$key = useItemMediaItems_sortedPhotos$key,
    Video extends ItemMediaVideoKey = ItemMediaVideoKey,
    ViewInRoom extends useItemMediaItems_viewInRoomPhotos$key =
        useItemMediaItems_viewInRoomPhotos$key,
> = MediaItemPhoto<Photos> | MediaItemVideo<Video> | MediaItemViewInRoom<ViewInRoom>;
type MediaItems<
    Photos extends useItemMediaItems_sortedPhotos$key = useItemMediaItems_sortedPhotos$key,
    Video extends ItemMediaVideoKey = ItemMediaVideoKey,
    ViewInRoom extends useItemMediaItems_viewInRoomPhotos$key =
        useItemMediaItems_viewInRoomPhotos$key,
> = MediaItem<Photos, Video, ViewInRoom>[];

const VIDEO_INDEX = 1;

export const isVimeoVideo = <Item extends { __type: string }>(
    item: Item
): item is Extract<Item, { __type: 'video'; vimeoId: unknown }> =>
    item.__type === 'video' && 'vimeoId' in item;

export const useItemMediaItems = <
    SortedPhotos extends useItemMediaItems_sortedPhotos$key,
    VideoVimeo extends useItemMediaItems_videoVimeo$key,
    MuxVideo extends useItemMediaItems_muxVideo$key,
    ViewInRoom extends useItemMediaItems_viewInRoomPhotos$key,
>({
    viewer: viewerRef,
    item: itemRef,
    sortedPhotos: sortedPhotosRef,
    videoVimeo: videoVimeoRef,
    muxVideo: muxVideoRef,
    viewInRoomPhotos: viewInRoomPhotosRef,
    selectedSkuId,
}: {
    viewer: useItemMediaItems_viewer$key;
    item: useItemMediaItems_item$key;
    sortedPhotos: SortedPhotos;
    videoVimeo: VideoVimeo | null | undefined;
    muxVideo: MuxVideo | null | undefined;
    viewInRoomPhotos: ViewInRoom;
    selectedSkuId?: string | null; // don't provide this prop if there is no need to sort photos by selected sku
}): MediaItems<SortedPhotos, NonNullable<VideoVimeo> | NonNullable<MuxVideo>, ViewInRoom> => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);

    const { isMultiSku } = item || {};

    const sortedPhotos = useFragment(sortedPhotosFragment, sortedPhotosRef);
    const videoVimeo = useFragment(vimeoVideFragment, videoVimeoRef);
    const muxVideo = useFragment(muxVideoFragment, muxVideoRef);
    const viewInRoomPhotos = useFragment(viewInRoomPhotosFragment, viewInRoomPhotosRef);

    const showVideo = useShowVimeoVideo({ viewer, item });

    const photos = (sortedPhotos || []).filter(filterNulls);
    const roomPhotos = (viewInRoomPhotos || []).filter(filterNulls);
    const sortedPhotosRefFiltered = sortedPhotosRef.filter(filterNulls);
    const lockedSortedPhotosRef = useRef<MediaItemPhotos>(
        photos.map<MediaItemPhoto>(
            (photo: ArrayElement<useItemMediaItems_sortedPhotos$data>, i) => ({
                __type: 'photo',
                ...sortedPhotosRefFiltered[i],
                largePath: encodeImageUrl(photo.largePath),
                masterOrZoomPath: encodeImageUrl(photo.masterOrZoomPath),
            })
        )
    );
    const prevPhotosRef = useRef<MediaItemPhotos>(lockedSortedPhotosRef.current);

    return useMemo(() => {
        let _mediaItems: MediaItems<
            SortedPhotos,
            NonNullable<VideoVimeo> | NonNullable<MuxVideo>,
            ViewInRoom
        > = [...prevPhotosRef.current];

        const selectedSkuPhotos: MediaItemPhotos = [];
        if (isMultiSku && selectedSkuId) {
            const productPhotos: MediaItemPhotos = [];
            const remainingPhotos: MediaItemPhotos = [];

            lockedSortedPhotosRef.current.forEach((photo, index) => {
                if (photos[index]?.imageSkuXrefs?.some(xref => xref?.skuId === selectedSkuId)) {
                    selectedSkuPhotos.push(photo);
                } else if (photos[index]?.imageSkuXrefs?.length === 0) {
                    productPhotos.push(photo);
                } else {
                    remainingPhotos.push(photo);
                }
            });

            if (selectedSkuPhotos.length !== 0) {
                prevPhotosRef.current = [
                    ...selectedSkuPhotos,
                    ...productPhotos,
                    ...remainingPhotos,
                ];
                _mediaItems = [...prevPhotosRef.current];
            }
        }

        if (showVideo && videoVimeo && videoVimeoRef) {
            const videoIndex = selectedSkuPhotos.length + VIDEO_INDEX;
            _mediaItems.splice(videoIndex, 0, {
                __type: 'video',
                ...videoVimeoRef,
            });
        }

        if (showVideo && muxVideo && muxVideoRef) {
            const videoIndex = selectedSkuPhotos.length + VIDEO_INDEX;
            _mediaItems.splice(videoIndex, 0, {
                __type: 'video',
                ...muxVideoRef,
            });
        }

        if (roomPhotos.length > 0) {
            _mediaItems.push(
                ...viewInRoomPhotosRef
                    .filter(filterNulls)
                    .map<
                        MediaItem<SortedPhotos, VideoVimeo | MuxVideo, ViewInRoom>
                    >(viewInRoomPhoto => ({
                        __type: 'viewInRoom',
                        ...viewInRoomPhoto,
                    }))
            );
        }

        return _mediaItems;
    }, [
        showVideo,
        videoVimeo,
        muxVideo,
        selectedSkuId,
        isMultiSku,
        photos,
        videoVimeoRef,
        muxVideoRef,
        viewInRoomPhotosRef,
        roomPhotos,
    ]);
};
